import React, { PropTypes, PureComponent } from 'react';
import classNames from 'classnames';
import { SyndicatedPlayerLoginOptions, PlayerPlacement, ContentStatus, Flags } from 'enums';
import WatchPropTypes from 'common/PropTypes';
import { authStateKnown } from 'lib/auth';
import { isEmpty } from 'lib/util';
import ResponsiveThumbnail from 'common/ResponsiveThumbnail';
import authenticationAware from 'webapp/common/authenticationAware';
import { contentUnSelect } from 'common/actions/appActions';
import { createClearPlayingStreamAction, createResetPlayerAction } from 'webapp/player/actions';
import { createDisplayProviderDialogAction, createCloseProviderDialogAction } from 'common/actions/authenticationActions';
import ProviderDialog from '../ProviderDialog';
import ConnectedPlayer from '../ConnectedPlayer';
import styles from './style';
import playable from '@alias/Playable';
import SyndicatedPlayerAPIProxy from '../SyndicatedPlayerAPIProxy';

const { SELF, NEW_WINDOW, NEW_WINDOW_MANAGED } = SyndicatedPlayerLoginOptions;
const IMAGE_WIDTHS = [400, 640, 1200, 1800];

export const domainAuthStatus = {
    UNKNOWN: 0,
    NOT_ALLOWED: 1,
    ALLOWED: 2
};

class SyndicatedPlayer extends PureComponent {
    constructor(props) {
        super(props);
        this.onContinueClick = this.onContinueClick.bind(this);
        this.onReloadClick = this.onReloadClick.bind(this);
        this.state = {
            isAuthenticated: false,
            authRetry: props.authRetry,
            xdomainBlocked: props.xdomainBlocked
        };
    }

    componentDidMount() {
        this.apiProxy = new SyndicatedPlayerAPIProxy(this);
    }

    componentWillReceiveProps(nextProps) {
        this.apiProxy.componentWillReceiveProps(nextProps);
        const {
            canPlayContent: nextCanPlayContent,
            userAuth: nextUserAuth,
            errorMessage: nextErrorMessage
        } = nextProps;
        const { dispatch, canPlayContent, userAuth, location: { query } } = this.props;
        if (
            query[Flags.AUTO_DISPLAY_PROVIDER_DIALOG] === 'true' &&
            !nextErrorMessage &&
            !authStateKnown(userAuth) &&
            authStateKnown(nextUserAuth) &&
            nextUserAuth.indexOf('mvpd') === -1
        ) {
            dispatch(createDisplayProviderDialogAction());
        }
        if (!nextCanPlayContent && canPlayContent) {
            dispatch(contentUnSelect());
            dispatch(createClearPlayingStreamAction());
            dispatch(createResetPlayerAction());
            dispatch(createCloseProviderDialogAction(false));
            if (this.state.isAuthenticated || this.state.authRetry) {
                this.setState({ isAuthenticated: false, authRetry: false });
            }
        }
    }

    componentDidUpdate({ content: prevContent }) {
        const { canPlayContent, content, clickHandler, player: { embedded } } = this.props;
        if (canPlayContent && embedded && prevContent.id !== content.id) {
            clickHandler();
        }
    }

    componentWillUnmount() {
        this.apiProxy.destroy();
    }

    onContinueClick(event, providerInfo) {
        const { loginOption, providerSelect } = this.props;

        if (loginOption === NEW_WINDOW) {
            return;
        }

        event.preventDefault();

        this.providerInfo = providerInfo;

        if (loginOption === SELF) {
            providerSelect(providerInfo);
        } else if (loginOption === NEW_WINDOW_MANAGED) {
            const loginWindow = window.open(event.currentTarget.href, 'AdobePassProxyLogin');
            if (!loginWindow) {
                console.warn('login proxy page blocked');
                return;
            }
            this.loginWindow = loginWindow;
        }
    }

    onReloadClick() {
        this.recheckAccess();
    }

    onAuthSuccess() {
        this.setState({
            isAuthenticated: true,
            authRetry: false,
            xdomainBlocked: false
        });
    }

    onLoginClose() {
        this.loginWindow = null;
    }

    onXDomainCheckFail() {
        this.props.providerSelect(this.providerInfo);
    }

    parentHost(host) {
        const { onParentHost } = this.props;
        if (onParentHost) {
            onParentHost(host);
        }
    }

    recheckAccess() { // eslint-disable-line class-methods-use-this
        this.apiProxy.reload();
    }

    render() {
        const { isAuthenticated, authRetry, xdomainBlocked } = this.state;
        const {
            providerDialog: { providers, display },
            content: { name, status, imageHref },
            playingStream,
            branding,
            location,
            player: { embedded },
            canPlayContent,
            domainAuth,
            errorMessage,
            adobePassLogout,
            clickHandler,
            onSocialOpened,
            onSocialClosed,
            onCaptionOpened,
            onCaptionClosed,
            providerSelect
        } = this.props;
        const showProviderDialog = display || authRetry;
        if (domainAuth === domainAuthStatus.UNKNOWN) {
            return null;
        }

        const playButtonClasses = classNames('button-play', 'button-play--video', {
            'button-play--live': (status === ContentStatus.LIVE)
        });
        const dialogViewItem = [];
        // `canPlayContent` will never be `true` when domain is not whitelisted
        // since it doesn't perform `createSmartAdobePassInitializeAction`
        if (!canPlayContent || !embedded || isEmpty(playingStream)) {
            dialogViewItem.push(
                <ResponsiveThumbnail
                    className="hero_img"
                    style={showProviderDialog ? { position: 'absolute', opacity: 0.2 } : {}}
                    src={imageHref}
                    srcSetWidths={IMAGE_WIDTHS}
                    sizes="100vw"
                    alt={name}
                    cquality={95}
                />
            );
        }
        let el;
        if (errorMessage) {
            el = <div style={styles.message}>{errorMessage}</div>;
        } else if (showProviderDialog) {
            el = (
                <ProviderDialog
                    providers={providers}
                    branding={branding}
                    isAuthenticated={isAuthenticated}
                    onContinueClick={this.onContinueClick}
                    onReloadClick={this.onReloadClick}
                    providerSelect={providerSelect}
                    authRetry={authRetry}
                    xdomainBlocked={xdomainBlocked}
                />
            );
        } else {
            el = <div className={playButtonClasses} />;
        }
        dialogViewItem.push(el);

        const needPlayer = canPlayContent && !errorMessage;
        const isAuthenticatableScreen = !errorMessage && !showProviderDialog && !embedded;

        const dialogView = (
            <div style={styles.dialogContainer}>
                {dialogViewItem}
                {needPlayer && (
                    <ConnectedPlayer
                        ref={player => { this.connectedPlayer = player; }}
                        playerPlacement={PlayerPlacement.HERO}
                        branding={branding}
                        location={location}
                        render={embedded}
                        handleLogout={adobePassLogout}
                        onSocialOpened={onSocialOpened}
                        onSocialClosed={onSocialClosed}
                        onCaptionOpened={onCaptionOpened}
                        onCaptionClosed={onCaptionClosed}
                    />
                )}
            </div>
        );

        return (
            <div
                className="playback-container hero_wrapper"
                style={styles.container}
                onClick={isAuthenticatableScreen && clickHandler}
            >
                {dialogView}
            </div>
        );
    }
}

SyndicatedPlayer.propTypes = {
    dispatch: PropTypes.func,
    content: WatchPropTypes.content,
    playingStream: WatchPropTypes.stream,
    userAuth: PropTypes.array,
    canPlayContent: PropTypes.bool,
    loginOption: PropTypes.number,
    providerDialog: PropTypes.object,
    providerSelect: PropTypes.func,
    branding: PropTypes.object,
    location: PropTypes.object,
    player: PropTypes.object,
    domainAuth: PropTypes.oneOf([
        domainAuthStatus.UNKNOWN,
        domainAuthStatus.ALLOWED,
        domainAuthStatus.NOT_ALLOWED
    ]),
    authRetry: PropTypes.bool,
    xdomainBlocked: PropTypes.bool,
    errorMessage: PropTypes.string,
    adobePassLogout: PropTypes.func.isRequired,
    clickHandler: PropTypes.func.isRequired,
    onParentHost: PropTypes.func,
    onSocialOpened: PropTypes.func,
    onSocialClosed: PropTypes.func,
    onCaptionOpened: PropTypes.func,
    onCaptionClosed: PropTypes.func
};

export default authenticationAware()(playable(SyndicatedPlayer));



// WEBPACK FOOTER //
// ./app/webapp/syndicated/SyndicatedPlayer/index.js